import type { CatalogComponentProperties } from '@/domain/catalog/common';
import { isFiniteNumber } from '@/util/isFiniteNumber';
import type { Url } from '@/domain/types';
import type { LinerSize } from '@/domain/catalog/liner';
import type { HeadSize } from '@/domain/catalog/head';

export enum CupSystem {
    // noinspection JSUnusedGlobalSymbols
    G7 = 'g7',
}

export enum CupType {
    // noinspection JSUnusedGlobalSymbols
    PPS = 'pps',
    NEUTRAL = 'neutral',
    HIGHWALL = 'highwall',
}

export const cupSystems = Object.values(CupSystem);
export type CupSystemValue = (typeof cupSystems)[number];

export function isCupSystem(value: string): value is CupSystemValue {
    return cupSystems.includes(value as CupSystemValue);
}

export const cupTypes = Object.values(CupType);
export type CupTypeValue = (typeof cupTypes)[number];

export function isCupType(value: string): value is CupTypeValue {
    return cupTypes.includes(value as CupTypeValue);
}

export enum CupSize {
    // noinspection JSUnusedGlobalSymbols
    C42 = 42,
    C44 = 44,
    C46 = 46,
    C48 = 48,
    C50 = 50,
    C52 = 52,
    C54 = 54,
    C56 = 56,
    C58 = 58,
    C60 = 60,
    C62 = 62,
    C64 = 64,
    C66 = 66,
    C68 = 68,
}
export const cupSizes = Object.values(CupSize).filter((value): value is number => typeof value === 'number');

/** Minimum cup size (mm) above which the -6mm CoCr head offset is selectable */
export const AVENIR_N6_HEADOFFSET_CUP_SIZE_THRESHOLD = CupSize.C44;

/** Minimum cup size (mm) above which dual-mobility is allowed */
export const DUAL_MOBILITY_CUP_SIZE_THRESHOLD = CupSize.C46;

/** Dual mobility is disabled when a cup is selected and is at or below the minimum size. */
export function isDualMobilityDisabledByCupSize(cupSize: number | undefined): boolean {
    return cupSize !== undefined && cupSize <= DUAL_MOBILITY_CUP_SIZE_THRESHOLD;
}

/**
 * The -6mm CoCr head offset is only selectable above the cup-size threshold and not under
 * dual mobility. With no cup selected it is allowed.
 */
export function isMinus6HeadOffsetAllowedForCup(
    cupSize: number | null,
    dualMobility: boolean,
): boolean {
    if (!cupSize) {
        return true;
    }
    return cupSize > AVENIR_N6_HEADOFFSET_CUP_SIZE_THRESHOLD && !dualMobility;
}

export function isCupSize(value: string | number): value is CupSize {
    const intValue = typeof value === 'string' ? parseInt(value) : value;
    return isFiniteNumber(intValue) && cupSizes.includes(intValue as CupSize);
}

export function isCupSizeString(value: string): value is `${CupSize}` {
    const size = parseInt(value)
    return isFiniteNumber(size) && isCupSize(size)
}

/** Properties of a type of cup, independent of its use in any particular case */
export type CatalogCup = CatalogComponentProperties & {
    /** The system the cup belongs, which is currently always 'g7' */
    system: CupSystemValue;

    /** The type of the cup */
    type: CupTypeValue;

    /** The size of the cup, which is the outer diameter of the cup as a string */
    size: `${CupSize}`;

    /** The outer diameter of the cup */
    outerDiameter: CupSize;

    collisionMask: Url;

    /** The corresponding liner-size */
    linerSize: LinerSize;

    /** The recommended head-size to use with this cup */
    recommendedHeadSize: HeadSize;
};
